import { useCallback } from 'react';
import ReactFlow, { Background, Controls } from 'reactflow';
import { Box, Button, Paper, styled, Stack, IconButton, Divider } from '@mui/material';

import 'reactflow/dist/style.css';

import WorkflowStepTypeButton from '@frontend/features/workflows/components/WorkflowStepTypeButton';
import { useSidebar } from '@frontend/features/dashboard/contexts/SidebarContext/hook';
import {
  closedSidebarWidthRem,
  openSidebarWidthRem,
} from '@frontend/features/dashboard/components/DashboardSidebar/constants';
import { WorkflowStepType } from '@bandido/shared/constants';

import { edgeTypes, stepTypes } from '../../constants';
import { useWorkflow } from '../WorkflowContext/hook';
import { Close, NearMe, PanTool, Save } from '@mui/icons-material';

const WorkflowActionButton = styled(Button)`
  min-width: 48px;
  width: 48px;
  height: 48px;
`;

export const WorkflowDiagram = () => {
  const {
    steps,
    edges,
    onConnect,
    saveWorkflow,
    hasInitialStep,
    onNodesChange,
    onEdgesChange,
    onStepDragStop,
    setSelectedStepId,
    onCreateStep,
  } = useWorkflow();

  const { isOpen } = useSidebar();

  const sidebarWidth = isOpen ? openSidebarWidthRem : closedSidebarWidthRem;

  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleCanvasClick = () => {
    setSelectedStepId(null);
  };

  return (
    <Box width={`calc(100vw - ${sidebarWidth}rem)`} height="calc(100vh - 64px)">
      <ReactFlow
        fitView
        nodes={steps}
        edges={edges}
        onDrop={onCreateStep}
        onDragOver={onDragOver}
        onConnect={onConnect}
        nodeTypes={stepTypes}
        onClick={handleCanvasClick}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={onStepDragStop}
      >
        <Controls />
        <Background />
        <Paper
          elevation={4}
          sx={{
            left: '35%',
            transform: 'translateX(-35%)',
            backgroundColor: (theme) => theme.palette.common.white,
            zIndex: 4,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bottom: 24,
            position: 'absolute',
          }}
        >
          <Box gap={2} display="flex" flexDirection="row" alignItems="center" alignSelf="stretch">
            <Stack direction="row">
              <Box display="flex" flexDirection="column">
                <IconButton sx={{ p: 1.5 }}>
                  <NearMe />
                </IconButton>
                <Divider />
                <IconButton sx={{ p: 1.5 }}>
                  <PanTool />
                </IconButton>
              </Box>
              <Divider orientation="vertical" flexItem />
            </Stack>
            <Stack gap={2} flexDirection="row">
              <WorkflowStepTypeButton disabled={hasInitialStep} variant={WorkflowStepType.INITIAL} />
              <WorkflowStepTypeButton variant={WorkflowStepType.CUSTOM} />
              <WorkflowStepTypeButton variant={WorkflowStepType.FINAL} />
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack gap={1} padding={(theme) => theme.spacing(2.5, 3, 2.5, 1)} direction="row">
              <WorkflowActionButton variant="outlined" color="error">
                <Close />
              </WorkflowActionButton>
              <WorkflowActionButton onClick={saveWorkflow} variant="outlined" color="primary">
                <Save />
              </WorkflowActionButton>
            </Stack>
          </Box>
        </Paper>
      </ReactFlow>
    </Box>
  );
};
